import { getDocumentReferenceForBinaryId } from '@ctw/shared/api/fhir/binaries';
import type { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import type { EpisodeOfCareModel } from '@ctw/shared/api/fhir/models/episode-of-care';
import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import { ContentCard } from '@ctw/shared/components/containers/content-card';
import { DefinitionList, type DefinitionListItem } from '@ctw/shared/components/definition-list';
import { Link } from '@ctw/shared/components/link';
import { ResponsiveSourceDocumentLink } from '@ctw/shared/content/CCDA/responsive-source-document-link';
import {
  RelatedDocumentsList,
  type RelatedDocumentsListItem,
} from '@ctw/shared/content/documents/related-documents-list';
import { PatientInfoLine } from '@ctw/shared/content/patients/patient-info-line';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { TransitionDrawerPatientProvider } from '@ctw/shared/context/patient-provider';
import type { UseResourceDrawerOpenDrawerArgs } from '@ctw/shared/context/resource-drawer-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { tw } from '@ctw/shared/utils/tailwind';
import { faExternalLinkAlt } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { get, groupBy, mapValues } from 'lodash-es';
import { useEffect, useState } from 'react';

export const episodeOfCareDrawerAction: UseResourceDrawerOpenDrawerArgs<'EpisodeOfCare'>['action'] =
  ({ model }) => ({
    label: 'Episode of Care',
    title: model.title,
    content: {
      body: model.patientUPID && (
        <TransitionDrawerPatientProvider patientUPID={model.patientUPID}>
          <div className={tw`w-full py-2`}>
            {!window.location.pathname.includes(model.patientUPID) &&
            model.patient &&
            model.patientUPID ? (
              <PatientHeader patient={model.patient} patientUPID={model.patientUPID} />
            ) : (
              <EpisodeOfCareHeader episodeOfCare={model} />
            )}
            <PatientInfoLine patient={model.patient} />
          </div>
          <div className={tw`flex flex-col gap-5`}>
            <ContentCard
              variant="focal"
              title="Details"
              content={{
                header: <ResponsiveSourceDocumentLink model={model} />,
              }}
            >
              <DefinitionList items={episodeOfCareData(model)} />
            </ContentCard>
            <RelatedBinaries model={model} />
          </div>
        </TransitionDrawerPatientProvider>
      ),
    },
  });

const episodeOfCareData = (toc: EpisodeOfCareModel): Array<DefinitionListItem> => {
  const diagnosisGroupsByRole = mapValues(
    groupBy(toc.resource.diagnosis, 'role.coding[0].code'),
    (group, code) => ({
      display: get(group, '[0].role.coding[0].display', ''),
      diagnoses: toc.diagnosesForRole(code),
    }),
  );

  const diagnosisListItems = Object.entries(diagnosisGroupsByRole || {}).map(([_, group]) => ({
    label: group.display,
    value:
      group.diagnoses.length > 0 ? <DiagnosisDetails diagnosis={group.diagnoses} /> : undefined,
  }));

  return [
    { label: 'Start Date', value: toc.periodStart },
    { label: 'End Date', value: toc.periodEnd },
    { label: 'Type', value: toc.class },
    { label: 'Location', value: toc.location },
    ...diagnosisListItems,
    { label: 'Discharge Disposition', value: toc.dischargeDisposition },
  ];
};

interface RelatedBinariesProps {
  model: EpisodeOfCareModel;
}

const RelatedBinaries = ({ model }: RelatedBinariesProps) => {
  const telemetry = useTelemetry();
  const { graphqlClient } = useCTW();

  const [isLoading, setIsLoading] = useState(model.dischargeBinaryIds.length > 0);
  const [relatedDocumentsListItems, setRelatedDocumentsListItems] = useState<
    Array<RelatedDocumentsListItem>
  >([]);

  useEffect(() => {
    if (model.dischargeBinaryIds.length > 0) {
      const documentReferencesPromises = model.dischargeBinaryIds.map((binaryId) =>
        getDocumentReferenceForBinaryId({
          upid: model.patientUPID ?? '',
          binaryId,
          graphqlClient,
          telemetry,
        }),
      );

      Promise.allSettled(documentReferencesPromises).then((results) => {
        setRelatedDocumentsListItems(
          results
            .map((result) => (result.status === 'fulfilled' ? result.value : undefined))
            .filter(
              (documentModel: DocumentModel | null | undefined) =>
                documentModel !== null && documentModel !== undefined,
            )
            .map((documentModel) => ({
              preferred: model.preferredDischargeBinaryIds.includes(
                documentModel.binaryId as never,
              ),
              document: documentModel satisfies DocumentModel,
            })),
        );
        setIsLoading(false);
      });
    }
  }, [model, graphqlClient, telemetry]);

  if (model.dischargeBinaryIds.length === 0) {
    return <></>;
  }

  return <RelatedDocumentsList isLoading={isLoading} items={relatedDocumentsListItems} />;
};

type PatientHeaderProps = {
  patient: PatientModel;
  patientUPID: string;
};

function PatientHeader({ patient, patientUPID }: PatientHeaderProps) {
  const { display, gender } = patient;

  return (
    <div className={tw`flex justify-between`}>
      {display}
      {gender ? ` (${gender[0].toUpperCase()})` : ''}
      {
        <Link
          href={`/patients/${patientUPID}`}
          className={tw`flex cursor-pointer items-center space-x-2 border-0 bg-transparent p-0 text-primary-text leading-none outline-none hover:text-primary-main`}
          target="_blank"
        >
          <FontAwesomeIcon icon={faExternalLinkAlt} className={tw`h-3`} />
          <span className={tw`normal-case`}>Go To Profile</span>
        </Link>
      }
    </div>
  );
}

function EpisodeOfCareHeader({ episodeOfCare }: { episodeOfCare: EpisodeOfCareModel }) {
  return <div className={tw`flex justify-between`}>{episodeOfCare.title}</div>;
}

type DiagnosisDetailsProps = {
  diagnosis: Array<string>;
};
function DiagnosisDetails({ diagnosis }: DiagnosisDetailsProps) {
  if (diagnosis.length === 1) {
    return <span>{diagnosis[0]}</span>;
  }

  return (
    <ul className={tw`list-inside list-disc`}>
      {diagnosis.map((d) => (
        <li key={d}>{d}</li>
      ))}
    </ul>
  );
}
